import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useTravelCardIsClaimable, useListBearers } from '@/api/trafikportalen';
import { FormElement } from '../FormElement';
import Input from '../core/Input';
import { Spinner } from '../core/Spinner';
import { IconCheck, IconXMarkCircle } from '@/icons/CoreIcons';
import Button from '../core/Button';
import { Bearer } from '../Bearer';
import {
  getApiErrorMessage,
  isResourceNotFoundError,
} from '@/helpers/apiError';
import { isValidEmail } from '@/helpers/validation';
import { IconSmartcard, IconSmartphoneDevice } from '@/icons';

type BearerType = 'card' | 'bobcatTraveller';

export interface TemporaryTravellerData {
  name: string;
  bearerId?: string;
  cardSerial?: string;
}

interface TemporaryTravellerFormProps {
  onTravellerDataChange: (data: TemporaryTravellerData | undefined) => void;
}

export const TemporaryTravellerForm = ({
  onTravellerDataChange,
}: TemporaryTravellerFormProps) => {
  const { formatMessage } = useIntl();
  const [name, setName] = useState('');
  const [bearerType, setBearerType] = useState<BearerType>('card');
  const [formattedCardSerial, setFormattedCardSerial] = useState('');
  const [cardSerial, setCardSerial] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [debouncedEmail, setDebouncedEmail] = useState('');
  const [selectedBearerId, setSelectedBearerId] = useState<string>('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedEmail(email);
    }, 300);

    return () => clearTimeout(timer);
  }, [email]);

  const {
    data: cardValidation,
    error: cardValidationError,
    isLoading: isLoadingCardValidation,
  } = useTravelCardIsClaimable(
    { serial: cardSerial ?? '' },
    {
      query: {
        enabled:
          bearerType === 'card' && !!cardSerial && cardSerial.length === 16,
      },
    },
  );

  const needsExistingBearer =
    bearerType === 'card' &&
    !!cardSerial &&
    cardSerial.length === 16 &&
    cardValidation?.claimable === false &&
    !cardValidationError;

  const isClaimableCard = cardValidation?.claimable === true;

  const cardError = useMemo(() => {
    if (cardValidationError) {
      return getApiErrorMessage(
        cardValidationError,
        formatMessage({ defaultMessage: 'Något gick fel.' }),
      );
    }
    return '';
  }, [cardValidationError, formatMessage]);

  const { bearerParams, fetchBearersEnabled } = useMemo((): {
    bearerParams: { email?: string; card_serial?: string };
    fetchBearersEnabled: boolean;
  } => {
    if (bearerType === 'card') {
      if (!needsExistingBearer) {
        return {
          bearerParams: {},
          fetchBearersEnabled: false,
        };
      }
      return {
        bearerParams: { card_serial: cardSerial ?? '' },
        fetchBearersEnabled: true,
      };
    }
    if (!isValidEmail(debouncedEmail)) {
      return {
        bearerParams: {},
        fetchBearersEnabled: false,
      };
    }
    return {
      bearerParams: { email: debouncedEmail },
      fetchBearersEnabled: true,
    };
  }, [bearerType, debouncedEmail, cardSerial, needsExistingBearer]);

  const {
    data: bearers,
    error: bearersError,
    isError: isBearersError,
    isLoading: isLoadingBearers,
  } = useListBearers(bearerParams, {
    query: {
      enabled: fetchBearersEnabled,
      retry: false,
    },
  });

  const emailAccountNotFound = isResourceNotFoundError(bearersError);

  const emailAccountMissing =
    bearerType === 'bobcatTraveller' &&
    fetchBearersEnabled &&
    !isLoadingBearers &&
    emailAccountNotFound;

  const emailAccountLookupFailed =
    bearerType === 'bobcatTraveller' &&
    fetchBearersEnabled &&
    !isLoadingBearers &&
    isBearersError &&
    !emailAccountNotFound;

  const emailAccountFound =
    bearerType === 'bobcatTraveller' &&
    fetchBearersEnabled &&
    !isLoadingBearers &&
    !isBearersError &&
    !!bearers;

  const emailLookupMessage = emailAccountMissing
    ? formatMessage({
        defaultMessage:
          'Det finns inget konto i reseappen med denna e-postadress.',
      })
    : emailAccountLookupFailed
      ? formatMessage({
          defaultMessage: 'Kunde inte kontrollera kontot i reseappen.',
        })
      : '';

  const cardHasExistingBearer = Boolean(bearers?.[0]?.id);
  const cardLookupError =
    bearerType === 'card' &&
    needsExistingBearer &&
    !isLoadingBearers &&
    !cardHasExistingBearer
      ? formatMessage({
          defaultMessage:
            'Kortet går inte att använda. Ingen bärare hittades för kortnumret.',
        })
      : '';

  const displayedCardError = cardError || cardLookupError;
  const isCardValid =
    bearerType === 'card' &&
    !!cardSerial &&
    cardSerial.length === 16 &&
    !displayedCardError &&
    (isClaimableCard || cardHasExistingBearer);

  useEffect(() => {
    if (bearerType === 'bobcatTraveller' && bearers && bearers.length > 0) {
      setSelectedBearerId(bearers[0].id);
    }
  }, [bearerType, bearers]);

  useEffect(() => {
    const trimmedName = name.trim();
    if (bearerType === 'card') {
      if (isCardValid && trimmedName && cardSerial && isClaimableCard) {
        onTravellerDataChange({
          name: trimmedName,
          cardSerial,
        });
      } else if (isCardValid && trimmedName && bearers?.[0]?.id) {
        onTravellerDataChange({
          name: trimmedName,
          bearerId: bearers[0].id,
          cardSerial: cardSerial ?? '',
        });
      } else {
        onTravellerDataChange(undefined);
      }
    } else if (trimmedName && selectedBearerId) {
      onTravellerDataChange({
        name: trimmedName,
        bearerId: selectedBearerId,
      });
    } else {
      onTravellerDataChange(undefined);
    }
  }, [
    bearerType,
    isCardValid,
    isClaimableCard,
    name,
    cardSerial,
    selectedBearerId,
    onTravellerDataChange,
    bearers,
  ]);

  const handleCardSerialChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      let value = e.target.value;

      if (value !== '0') {
        value = value.replace(/\D/g, '');
      }

      value = value.slice(0, 16);

      const formattedValue = value.replace(/(\d{4})(?=\d)/g, '$1 ');

      setFormattedCardSerial(formattedValue);
      setCardSerial(value);
    },
    [],
  );

  const handleNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setName(e.target.value);
    },
    [],
  );

  return (
    <div className="flex flex-col gap-4">
      <FormElement
        label={<FormattedMessage defaultMessage="Namn" />}
        required
        input={<Input type="text" value={name} onChange={handleNameChange} />}
      />

      <div className="flex flex-wrap gap-2">
        <Button
          variant={bearerType === 'card' ? 'filled' : 'outline'}
          className="min-w-max flex-1"
          onClick={() => {
            setBearerType('card');
            setSelectedBearerId('');
          }}
          startIcon={<IconSmartcard />}
        >
          <FormattedMessage defaultMessage="Kort" />
        </Button>
        <Button
          variant={bearerType === 'bobcatTraveller' ? 'filled' : 'outline'}
          className="min-w-max flex-1"
          onClick={() => {
            setBearerType('bobcatTraveller');
            setCardSerial(null);
            setFormattedCardSerial('');
          }}
          startIcon={<IconSmartphoneDevice />}
        >
          <FormattedMessage defaultMessage="E-post i appen / Mina sidor" />
        </Button>
      </div>

      {bearerType === 'card' ? (
        <FormElement
          id="card_serial"
          label={<FormattedMessage defaultMessage="Kortnummer" />}
          required
          input={
            <Input
              id="card_serial"
              type="text"
              placeholder={formatMessage({
                defaultMessage: '1234 1234 1234 1234',
              })}
              value={formattedCardSerial}
              onChange={handleCardSerialChange}
              endAdornment={
                isLoadingCardValidation ||
                (needsExistingBearer && isLoadingBearers) ? (
                  <Spinner className="size-4" />
                ) : isCardValid ? (
                  <IconCheck />
                ) : null
              }
              error={displayedCardError}
              helperTextReserveSpace
            />
          }
        />
      ) : (
        <>
          <FormElement
            id="email"
            label={<FormattedMessage defaultMessage="E-post" />}
            required
            input={
              <Input
                id="email"
                type="email"
                placeholder={formatMessage({
                  defaultMessage: 'example@example.com',
                })}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                endAdornment={
                  isLoadingBearers ? (
                    <Spinner className="size-4" />
                  ) : emailAccountFound && (bearers?.length ?? 0) > 0 ? (
                    <IconCheck />
                  ) : emailAccountMissing ? (
                    <IconXMarkCircle className="size-4 text-red-700" />
                  ) : null
                }
                error={emailLookupMessage}
                helperTextReserveSpace
              />
            }
          />
          {emailAccountFound && (
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium" htmlFor="temp-bearer-list">
                <FormattedMessage defaultMessage="Välj bärare" />
                <span> *</span>
              </label>
              <ul id="temp-bearer-list" className="flex flex-col gap-1">
                {bearers?.length === 0 && (
                  <i className="text-text-700">
                    <FormattedMessage defaultMessage="Kontot finns i reseappen men har inga bärare kopplade." />
                  </i>
                )}
                {bearers?.map((bearer) => {
                  const isSelected = selectedBearerId === bearer.id;
                  return (
                    <li key={bearer.id}>
                      <Button
                        className="h-16 w-full px-2 font-normal"
                        variant={isSelected ? 'filled' : 'outline'}
                        onClick={() => setSelectedBearerId(bearer.id)}
                      >
                        <Bearer bearer={bearer} />
                      </Button>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
};
